(ns admin.components.composite.questionnaire.questions
  (:require
   [re-frame.core :as re-frame]
   [reagent.core :as reagent]
   [domestic.core :as d]
   [admin.utils :as au]
   [admin.libs.flip-move :refer [FlipMove]]

   [kyber.lib.kyber-db :as kyber-db]
   [kyber.components.base.button :as button]
   [kyber.components.base.text :as text]
   [kyber.styles.core :refer [gs styles->classes]]

   [admin.components.composite.question-editor.question-selector :as question-selector]
   [admin.components.composite.question-editor.core :as question-editor]
   [admin.components.composite.questionnaire.question-card :as question-card]
   [admin.router.utils :as aru]))

(def classes
  (styles->classes
   {:empty-state-container
    {:display :flex
     :flex-direction :column
     :align-items :center
     :padding-top (gs [:spacing :p28])
     :padding-bottom (gs [:spacing :p28])
     :border-top (str "1px solid " (gs [:colors :gray-scale-200]))
     :border-bottom (str "1px solid " (gs [:colors :gray-scale-200]))}

    :add-question-cta
    {:display :flex
     :justify-content :center
     :align-items :center
     :position :relative
     :z-index 1
     :background-color (gs [:colors :true-white])}

    :movement-controls
    {:display :flex
     :flex-direction :column
     :margin-right (gs [:spacing :p20])}

    :question-container
    {:display :flex
     :align-items :center
     :margin-bottom (gs [:spacing :p20])}

    :evaluation-question-container
    {:justify-content :center}

    :shared-question-container
    {:justify-content :center}

    :pre-test-question-container
    {:justify-content :flex-start}

    :post-test-question-container
    {:justify-content :flex-end}

    :assessment-background-container
    {:position :absolute
     :width "100%"
     :height "100%"
     :z-index 0
     :display :flex}

    :pre-test-background
    {:width "50%"
     :height "100%"
     :background-color (gs [:colors :gray-scale-100])}

    :post-test-background
    {:width "50%"
     :height "100%"
     :background (gs [:colors :primary-lite-200])}

    :move-question-button
    {:height "100%"
     :background (gs [:colors :true-white])
     :padding (gs [:spacing :p8])
     :box-shadow (gs [:shadows :shadow-12])}

    :pre-post-test-banner
    {:display :flex
     :justify-content :space-between
     :padding [(gs [:spacing :p8])
               (gs [:spacing :p20])]
     :border (str "1px solid " (gs [:colors :gray-scale-200]))
     :border-radius [(gs [:radius :r3])
                     (gs [:radius :r3])
                     (gs [:radius :r0])
                     (gs [:radius :r0])]}

    :add-button-container
    {:padding [(gs [:spacing :p8])
               (gs [:spacing :p20])]
     :border (str "1px solid " (gs [:colors :gray-scale-200]))
     :border-radius [(gs [:radius :r0])
                     (gs [:radius :r0])
                     (gs [:radius :r3])
                     (gs [:radius :r3])]}}))

(defn- new-question-state
  [question-type]
  (assoc (au/question-type->edit-state question-type)
         :question/uuid nil))

(def new-question-context-state
  {:question.context/uuid nil
   :step :question ;; #{:question :releated-details :display-settings}
   :question.context/question nil ;; #{:uuid :type :has-correctness?}
   ;; Maybe Optional?
   :questionnaire-uuid nil
   :questionnaire-type nil
   :activity-uuid nil
   :curriculum-uuid nil})

(d/defdispatcher dispatcher {})

(d/defevent dispatcher :add-question
  [state activity-context]
  (reset! state (merge new-question-context-state activity-context)))

(d/defevent dispatcher :edit-question-context
  [state question-context-state]
  (reset! state (assoc question-context-state :step :question)))

(d/defevent dispatcher :set-question-type
  [state question-type]
  (swap! state assoc :question.context/question (new-question-state question-type)))

(d/defevent dispatcher :set-step
  [state step]
  (swap! state assoc :step step))

(d/defevent dispatcher :set-question-context-uuid
  [state question-context-uuid]
  (swap! state assoc :question.context/uuid question-context-uuid))

(d/defevent dispatcher :set-question-uuid
  [state question-uuid]
  (swap! state assoc-in [:question.context/question :question/uuid] question-uuid))

(d/defevent dispatcher :cancel-edit
  [state _]
  (reset! state nil))

(defn- render-add-question-edit-settings-buttons
  [{:keys [questionnaire-is-updating on-add-new-question on-edit-settings questionnaire-type]}]
  [:div {:class (:add-question-cta classes)}
   [button/icon-button-xl
    {:on-click on-add-new-question
     :disabled questionnaire-is-updating}
    :plus-circle]
   [button/button-link
    {:on-click on-add-new-question
     :disabled questionnaire-is-updating
     :style {:margin-right (gs [:spacing :p48])}}
    "Add Question"]
   
   (when (= questionnaire-type :assessments)
     [:<>
     [button/icon-button-xl
      {:on-click on-edit-settings}
      :edit]
     [button/button-link
      {:on-click on-edit-settings}
      "Edit Settings"]])])

(defn- render-questionnaire-empty-state
  [props]
  [:div {:class (:empty-state-container classes)}
   [text/body-30 :p "To get started, add your first question."]
   [render-add-question-edit-settings-buttons props]])

(defn- render-question
  [{:keys [question-context
           learning-objective-label
           questionnaire-type
           activity-uuid
           allow-unmatched-graded-questions
           on-remove-question
           on-edit-question
           nq
           questionnaire-api
           questionnaire-is-updating
           questionnaire]}]
  (let [{:keys [idx
                is-hovering]
         :question.context/keys [question
                                 resources
                                 confidence-enabled?
                                 is-required?
                                 learning-objective
                                 assessment-type]}
        question-context

        update-question-context-event
        (:question-ctx-update-event questionnaire-api)]
    [:div {:class [(:question-container classes)
                   (case assessment-type
                     "both" (:shared-question-container classes)
                     "pre-test" (:pre-test-question-container classes)
                     "post-test" (:post-test-question-container classes)
                     nil)
                   (when (= questionnaire-type :evaluation)
                     (:evaluation-question-container classes))]}

     ;; Business decision made 11JUL19
     ;; No Longer prevent questions with correctness from exclusively
     ;; being on both pre & post test.

     ;; Business decision made 26AUG19
     ;; Allow preventing pre & post test mismatched questions (As a
     ;; superuser meta setting on the activity).
     (when (and (contains? #{"both" "post-test"} assessment-type)
                (or (false? (:question/has-correctness? question))
                    (true? allow-unmatched-graded-questions)))
       [:div {:class (:move-question-button classes)}
        [button/icon-button-xl
         {:disabled questionnaire-is-updating
          :on-click #(re-frame/dispatch
                      [update-question-context-event
                       (merge {:activity/uuid activity-uuid}
                              (cond-> (dissoc question-context :question.context/resources)
                                (nil? confidence-enabled?)
                                (dissoc :question.context/confidence-enabled?)

                                (nil? is-required?)
                                (dissoc :question.context/is-required?)

                                (= assessment-type "both")
                                (assoc :question.context/assessment-type "pre-test")

                                (= assessment-type "post-test")
                                (assoc :question.context/assessment-type "both")))])}
         :chevron-left]])

     [question-card/render
      {:idx idx
       :is-hovering is-hovering
       :activity-uuid activity-uuid
       :question question
       :resources resources
       :confidence confidence-enabled?
       :required is-required?
       :hide-recommendations (= questionnaire-type :evaluation)
       :learning-objective learning-objective
       :learning-objective-label learning-objective-label
       :scale-card (= :assessments questionnaire-type)
       :question-context-uuid (:question.context/uuid question-context)
       :questionnaire-type questionnaire-type
       :questionnaire-api questionnaire-api
       :on-remove-question on-remove-question
       :on-edit-question #(on-edit-question question-context)
       :questionnaire-is-updating questionnaire-is-updating
       :questionnaire-uuid ((nq :uuid) questionnaire)}]

     (when (and (contains? #{"both" "pre-test"} assessment-type)
                (or (false? (:question/has-correctness? question))
                    (true? allow-unmatched-graded-questions)))
       [:div {:class (:move-question-button classes)}
        [button/icon-button-xl
         {:disabled questionnaire-is-updating
          :on-click #(re-frame/dispatch
                      [update-question-context-event
                       (merge {:activity/uuid activity-uuid}
                              (cond-> (dissoc question-context :question.context/resources)
                                (nil? confidence-enabled?)
                                (dissoc :question.context/confidence-enabled?)

                                (nil? is-required?)
                                (dissoc :question.context/is-required?)

                                (= assessment-type "both")
                                (assoc :question.context/assessment-type "post-test")

                                (= assessment-type "pre-test")
                                (assoc :question.context/assessment-type "both")))])}
         :chevron-right]])]))

(defn- render-questionnaire-question-list
  [{:keys [questionnaire-uuid activity-uuid curriculum-uuid on-edit-question questionnaire-api nq]}]
  (let [activity (re-frame/subscribe [::kyber-db/get-record {:model :activity
                                                             :ident activity-uuid}])]
    (fn [{:keys [questionnaire
                 questionnaire-type
                 activity-uuid
                 questionnaire-is-updating]}]
      (let [{:activity/keys [allow-unmatched-graded-questions learning-objectives]
             :or {allow-unmatched-graded-questions false}} @activity

            question-ctx-delete-event
            (:question-ctx-delete-event questionnaire-api)

            questions ((nq :questions) questionnaire)

            last-question-idx (dec (count questions))
            
            ordered-questions (map-indexed #(assoc %2 :idx %1) questions)]
        [FlipMove {:type-name "ul"
                   :style {:padding (gs [:spacing :p20])
                           :position :relative}}
         (for [{question-context-uuid :question.context/uuid
                learning-objective :question.context/learning-objective
                question-idx :idx :as question}
               ordered-questions]
           ^{:key question-context-uuid}
           [:li
            [render-question
             {:questionnaire-is-updating
              questionnaire-is-updating

              :allow-unmatched-graded-questions
              allow-unmatched-graded-questions

              :questionnaire
              questionnaire

              :question-context
              (cond-> question
                (= question-idx last-question-idx)
                (assoc :is-last-question true))

              :questionnaire-type
              questionnaire-type

              :questionnaire-api
              questionnaire-api

              :nq
              nq

              :curriculum-uuid
              curriculum-uuid

              :activity-uuid
              activity-uuid

              :learning-objective-label
              (->> learning-objectives
                   (filter #(= (:learning-objective/uuid %) (:learning-objective/uuid learning-objective)))
                   first
                   :learning-objective/label)

              :on-edit-question
              on-edit-question

              :on-remove-question
              (fn []
                (re-frame/dispatch
                 [question-ctx-delete-event
                  (cond-> {:activity/uuid activity-uuid
                           :question.context/uuid question-context-uuid}
                    
                    (not= questionnaire-type :assessments)
                    (assoc :questionnaire/uuid questionnaire-uuid))
                  {:on-success-toast "Question removed."
                   :on-failure-toast (str "Failed to delete your question. "
                                          "Please try again.")}]))}]])]))))

(defn- is-editing?
  [local-state]
  (-> @local-state some?))

(defn- show-question-selection-form?
  "If local state is in 'Edit Mode' (local state exists), has the question been created?"
  [local-state]
  (and (is-editing? local-state)
       (-> @local-state :question.context/question :question/type nil?)))

(defn- show-question-workflow?
  "If local state is in 'Edit Mode' and there is a selected question type, show workflow."
  [local-state]
  (and (is-editing? local-state)
       (-> @local-state :question.context/question :question/type some?)))

(defn- update-question-context
  [{:keys [local-state dispatch questionnaire-api]}
   {:keys [learning-objective confidence close-editor?]
    question-uuid :question/uuid}
   question]
  (let [{:keys [questionnaire-type questionnaire-uuid activity-uuid]} @local-state
        is-new-question (-> @local-state :question.context/question :question/uuid nil?)
        has-existing-context (-> @local-state :question.context/uuid some?)
        create-question-ctx-event (:question-ctx-create-event questionnaire-api)
        update-question-ctx-event (:question-ctx-update-event questionnaire-api)
        assessment-question-context-action (if has-existing-context
                                             update-question-ctx-event
                                             create-question-ctx-event)]
    
    (when is-new-question
      (dispatch [:set-question-uuid question-uuid]))

    (re-frame/dispatch
     [assessment-question-context-action
      (cond-> {:activity/uuid activity-uuid
               :question/uuid (:question/uuid question)}
        
        (and (not= questionnaire-type :assessments)
             (some? questionnaire-uuid))
        (assoc :questionnaire/uuid questionnaire-uuid) ;;Only needed for evals and floating

        (true? has-existing-context)
        (assoc :question.context/uuid (:question.context/uuid @local-state))

        (some? confidence)
        (assoc :question.context/confidence-enabled? confidence)

        (some? learning-objective)
        (assoc-in [:question.context/learning-objective :learning-objective/uuid] learning-objective)
        
        (nil? learning-objective)
        (assoc :question.context/learning-objective nil))
      {:response-keys [:question.context/uuid
                       :question.context/learning-objective
                       :question.context/confidence-enabled?
                       :question.context/is-required?
                       :question.context/assessment-type]
       :on-success (fn [question-context]
                     (dispatch [:set-question-context-uuid (:question.context/uuid question-context)])
                     (dispatch [:set-step (if (or (= :evaluation questionnaire-type)
                                                  (and (= (:step @local-state) :related-details)
                                                       (-> @local-state :question.context/question :question/has-correctness? not)))
                                            :display-settings
                                            :related-details)])
                     (when close-editor? 
                       (dispatch [:cancel-edit])))
       :on-success-toast (if has-existing-context "Question updated." "Question added!")
       :on-failure-toast (if has-existing-context
                           (str "Failed to update your question. "
                                "Please try again.")
                           (str "Failed to create "
                                (name questionnaire-type)
                                " question."))}])))

(defn- filter-questionnaire-question-context
  [question-context-uuid questionnaire nq]
  (->> ((nq :questions) questionnaire)
       (filter #(= (:question.context/uuid %) question-context-uuid))
       first))

(defn render
  [_]
  (let [local-state (reagent/atom nil)
        dispatch (d/bind-dispatcher dispatcher local-state)]
    (fn [{:keys [questionnaire-type
                 questionnaire-uuid
                 learning-objectives
                 questionnaire-is-updating
                 route-key
                 activity-uuid
                 curriculum-uuid
                 questionnaire-api
                 questionnaire]} nq]
      (let [enriched-question-context-state
            (merge @local-state
                   (some-> @local-state :question.context/uuid (filter-questionnaire-question-context questionnaire nq)))
            
            edit-settings-nav-to-url
            (aru/admin-path :activity-assessments
                            :curriculum-uuid curriculum-uuid
                            :activity-uuid activity-uuid
                            :question-step "settings")

            add-question-edit-settings-props
            {:questionnaire-is-updating @questionnaire-is-updating
             :on-add-new-question #(dispatch [:add-question {:questionnaire-uuid questionnaire-uuid
                                                             :questionnaire-type questionnaire-type
                                                             :activity-uuid activity-uuid
                                                             :curriculum-uuid curriculum-uuid}])
             :on-edit-settings #(re-frame/dispatch [:router/nav-to edit-settings-nav-to-url])
             :questionnaire-type questionnaire-type}

            question-context-update-event
            (:question-ctx-update-event questionnaire-api)]
        (cond
          (show-question-selection-form? local-state)
          [question-selector/render
           {:question-types (if (= route-key :activity-evaluation)
                              #{:single-answer-ungraded
                                :multiple-answer-ungraded
                                :free-response
                                :rating-scale}
                              #{:single-answer-graded
                                :multiple-answer-graded
                                :single-answer-ungraded
                                :multiple-answer-ungraded
                                :free-response
                                :rating-scale})
            :on-question-type-selection #(dispatch [:set-question-type %])
            :on-cancel #(dispatch [:cancel-edit])
            :questionnaire-type questionnaire-type}]

          (show-question-workflow? local-state)
          [:div {:style {:max-width "600px" :margin :auto}}
           [question-editor/render
            {:question-context enriched-question-context-state
             :questionnaire-type questionnaire-type
             :questionnaire-uuid questionnaire-uuid
             :questionnaire-api questionnaire-api
             :can-adjust-requiredness (or (contains? #{:floating :evaluation :assessments} questionnaire-type)
                                          (-> @local-state :question.context/question :question/has-correctness? false?))
             :on-cancel #(dispatch [:cancel-edit])
             :on-remove-resource (fn [{question-context-uuid :question.context/uuid} {resource-uuid :resource/uuid}]
                                   (re-frame/dispatch
                                    [:questionnaires/questionnaire-question-remove-resource
                                     {:activity-uuid activity-uuid
                                      :questionnaire-type questionnaire-type
                                      :questionnaire-uuid questionnaire-uuid
                                      :question-context-uuid question-context-uuid
                                      :resource-uuid resource-uuid}]))
             :on-settings-change #(re-frame/dispatch
                                   [question-context-update-event
                                    (merge {:activity/uuid activity-uuid}
                                           (cond-> (assoc % :question.context/uuid (-> @local-state :question.context/uuid))
                                             (nil? (:question.context/is-required? %))
                                             (assoc :question.context/is-required? false)
                                             
                                             (= questionnaire-type :floating)
                                             (assoc :questionnaire/uuid questionnaire-uuid)))
                                    {:on-success (fn [] (dispatch [:cancel-edit]))
                                     :on-success-toast "Question updated."
                                     :on-failure-toast (str "Failed to update your question. "
                                                            "Please try again.")}])
             :on-added-resource (fn [{question-context-uuid :question.context/uuid}
                                     {resource-uuid :resource/uuid}
                                     {learning-objective :learning-objective}]
                                  (re-frame/dispatch
                                   [:questionnaires/questionnaire-question-add-resource
                                    {:questionnaire-type questionnaire-type
                                     :questionnaire-uuid questionnaire-uuid
                                     :activity-uuid activity-uuid
                                     :curriculum-uuid curriculum-uuid
                                     :question-context-uuid question-context-uuid
                                     :learning-objective learning-objective
                                     :resource-uuid resource-uuid}]))
             :on-step-change #(dispatch [:set-step %])
             :on-question-submit (partial update-question-context {:local-state local-state :dispatch dispatch :questionnaire-api questionnaire-api})
             :learning-objectives learning-objectives
             :hide-educator-feedback (= questionnaire-type :evaluation)
             :is-updating questionnaire-is-updating
             :activity-uuid activity-uuid
             :curriculum-uuid curriculum-uuid
             :show-related-details-section (not= questionnaire-type :evaluation)}]]

          ;; Render question list
          (and (some? ((nq :questions) questionnaire))
               (-> ((nq :questions) questionnaire) count pos?))
          [:div
           (when (= :assessments questionnaire-type)
             [:div {:class (:pre-post-test-banner classes)}
              [text/title-40 :p {:style {:text-align :center}} "Pre-Test"]
              [text/title-40 :p {:style {:text-align :center}} "Post-Test"]])

           [:div {:style {:position :relative}}
            (when (= :assessments questionnaire-type)
              [:div {:class (:assessment-background-container classes)}
               [:div {:class (:pre-test-background classes)}]
               [:div {:class (:post-test-background classes)}]])

            [render-questionnaire-question-list
             (assoc {:questionnaire-type questionnaire-type
                     :questionnaire-uuid questionnaire-uuid
                     :questionnaire-api questionnaire-api
                     :activity-uuid activity-uuid
                     :curriculum-uuid curriculum-uuid
                     :questionnaire-is-updating @questionnaire-is-updating
                     :on-edit-question #(dispatch [:edit-question-context
                                                   {:question.context/uuid (:question.context/uuid %)
                                                    :question.context/question (select-keys (:question.context/question %) [:question/uuid :question/type])
                                                    :questionnaire-uuid ((nq :uuid) questionnaire)
                                                    :questionnaire-type questionnaire-type
                                                    :activity-uuid activity-uuid
                                                    :curriculum-uuid curriculum-uuid}])}
                    :questionnaire questionnaire
                    :nq nq)]]

           [:div {:class (:add-button-container classes)}
            [render-add-question-edit-settings-buttons add-question-edit-settings-props]]]

          ;; Render no questions added view
          :else
          [render-questionnaire-empty-state add-question-edit-settings-props])))))
